/**
 * The record flyout — one shell for every "New …" and "Edit …" in the product.
 *
 * `docs/record-flyouts.md` §2, from `24-record-flyouts/html/flyout-new-material.html`:
 * 620px from the right over a scrim, a 36px kind tile beside a 17/700 title
 * and a 12.5 muted path, the fields between, and a sticky footer carrying a
 * keyboard note on the left and Cancel · Save & add another · the primary on
 * the right.
 *
 * It replaces `SlideOver` for creating and editing records. `SlideOver` is
 * 400px with a `text-card` title and a plain footer — fine for a read-only
 * tray, too narrow for a form whose fields sit two to a row, and it has none
 * of the three behaviours a form needs:
 *
 * **Escape does not throw work away.** Closing a dirty sheet asks, with three
 * answers rather than two: discard, keep editing, or save. A confirm dialog
 * offering only OK and Cancel makes somebody who meant to save press Cancel
 * and lose it anyway.
 *
 * **⌘↵ saves from any field**, so a form can be finished without reaching for
 * the mouse, and the footer says so rather than leaving it to be discovered.
 *
 * **Save is never disabled.** A greyed-out Save is a form refusing to say
 * what is wrong; pressing it should take you to the first thing that is.
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { Button } from './primitives.js'
import { ConfirmDialog, escapeIsFor, watchOverlayKeys } from './overlays.js'
import { KindTile, type TileKind } from '../builder/kind-tile.js'
import { IconClose } from '../frame/icons.js'

export interface SheetProps {
  open: boolean
  /** Called when the sheet should go away — after the dirty check. */
  onClose: () => void
  kind: TileKind
  /**
   * A tile of this sheet's own, instead of the kind's.
   *
   * For a sheet whose subject is not a record: Admin › Integrations edits
   * Stripe's keys, and Stripe is not a `RecordKind`. Adding one to
   * `RECORD_ICONS` to satisfy a header would have put a vendor in the table
   * that the nav rail, the lookups and every builder row read from.
   */
  mark?: ReactNode
  title: string
  /** The path this record lives on — "Catalog · Materials". */
  sub?: string
  /** 620 for a record, 480 for a short form. Full width on a phone. */
  width?: 620 | 480
  /** The left of the footer. Defaults to the ⌘↵ note. */
  note?: ReactNode
  /** Between Cancel and the primary — "Save & add another". */
  secondary?: ReactNode
  /** The one primary action. */
  primary: ReactNode
  /**
   * The footer at the New request boards' size — 14 px tall padding, 10 px
   * between buttons, Cancel at 36 px — for a sheet whose own buttons are drawn
   * that size. Off, the footer is exactly as every other sheet draws it.
   */
  roomyFooter?: boolean
  /** Whether closing should ask first. */
  dirty?: boolean
  /** How many changes, for the dialog's sentence. */
  changeCount?: number
  /** What the dialog calls this record — "Save material". */
  saveLabel?: string
  /** ⌘↵, and the dialog's Save. */
  onSave?: () => void
  /**
   * Which layer this sheet sits on. 50 is an ordinary flyout over the page.
   *
   * A form opened *from* a form — "Create a new property" at the bottom of
   * the Property lookup on a new contact — has to sit over the one that
   * asked for it, so `RecordLookup` opens its nested form at 70. The
   * unsaved-changes dialog rides the same number, because a dialog that
   * drew underneath its own sheet would be unclickable.
   *
   * The floating panels — `ComboInput`'s menu and `MenuSelect`'s — sit at 80
   * so that a lookup inside *either* sheet is above both. There is no single
   * layer scale in the product yet; these four numbers are the ones that have
   * to agree, and they are written down in these two files.
   */
  layer?: number
  /**
   * A second column to the left of the form, inside the same panel
   * (`new-request.md` §16.1): New request's calendars. On `surface-alt` with a
   * rule on its right, the full height of the panel, and only where the window
   * is at least 1,180 px wide — narrower, the caller shows it some other way.
   * Left out, the sheet is exactly what it always was.
   */
  aside?: ReactNode
  /** How wide the aside is; the panel grows by this much. */
  asideWidth?: 560
  children: ReactNode
}

/** The narrowest window that has room for an aside beside a 620 form. */
export const SHEET_ASIDE_MIN_WINDOW = 1180

function useWideEnoughForAside(): boolean {
  const query = `(min-width: ${SHEET_ASIDE_MIN_WINDOW}px)`
  const [wide, setWide] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const change = () => setWide(list.matches)
    list.addEventListener('change', change)
    return () => list.removeEventListener('change', change)
  }, [query])
  return wide
}

export function Sheet({
  open, onClose, kind, mark, title, sub, width = 620, note, secondary, primary, roomyFooter,
  dirty = false, changeCount, saveLabel = 'Save', onSave, layer = 50, aside,
  asideWidth = 560, children,
}: SheetProps) {
  const phone = usePhone()
  const wideEnough = useWideEnoughForAside()
  const showAside = Boolean(aside) && !phone && wideEnough
  const panel = useRef<HTMLDivElement>(null)
  const [asking, setAsking] = useState(false)

  /* Closing with changes asks; closing a clean form just closes. */
  const attemptClose = useCallback(() => {
    if (dirty) { setAsking(true); return }
    onClose()
  }, [dirty, onClose])

  /*
   * Escape and ⌘↵ from inside the panel, heard on the window as the key
   * bubbles out, so a menu or a combo inside the form can swallow Escape for
   * itself first — closing the whole sheet because somebody dismissed a
   * dropdown would be the worst kind of surprise. It used to listen on the
   * panel itself, which a native listener reaches *before* React's root does,
   * so a field's `event.stopPropagation()` arrived after the sheet had already
   * asked "Close without saving?" (found in the browser, step 28).
   */
  useEffect(() => {
    if (!open) return
    const node = panel.current
    if (!node) return
    watchOverlayKeys()
    const onKey = (event: KeyboardEvent) => {
      if (!(event.target instanceof Node) || !node.contains(event.target)) return
      if (event.key === 'Escape') {
        /*
         * A popover opened over the sheet (the service pop-out's picker, at
         * layer 60) renders inside this panel, so its Escape arrives here
         * too, after it has already closed itself. It was its Escape.
         */
        if (!escapeIsFor(layer)) return
        event.stopPropagation()
        attemptClose()
        return
      }
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onSave?.()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, attemptClose, onSave, layer])

  /*
   * Focus the first field on open. Not the panel: landing on the container
   * means the first Tab goes to the close button, and a form you have to Tab
   * into is a form you reach for the mouse to start.
   *
   * The 30ms wait is for the panel to be laid out — but it is also 30ms in
   * which somebody can click a different field, and a form that yanks the
   * caret out of the box you just clicked is worse than one that starts
   * nowhere. So it only takes focus if focus has not already moved inside.
   */
  useEffect(() => {
    if (!open) return
    const timer = window.setTimeout(() => {
      const node = panel.current
      if (!node) return
      if (node.contains(document.activeElement) && document.activeElement !== node) return
      const first = node.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled])')
      first?.focus()
      if (first instanceof HTMLInputElement) first.select()
    }, 30)
    return () => window.clearTimeout(timer)
  }, [open])

  if (!open) return null

  const header = (
    <header className={cn('flex shrink-0 items-center gap-3 border-b border-line px-5 py-3.5')}>
      {mark ?? <KindTile kind={kind} size={36} />}
      <div className="flex min-w-0 flex-col gap-0.5">
        <b className="truncate text-[17px] font-bold tracking-[-0.01em] text-text">{title}</b>
        {sub && <span className="truncate text-[12.5px] text-text-muted">{sub}</span>}
      </div>
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        {/*
          * The keycap says how to leave without reading the manual. It is
          * decoration on a phone, where there is no Escape key and the bar
          * carries Cancel instead.
          */}
        <kbd className={cn('rounded-tag border border-line px-1.5 py-0.5 text-[10.5px]',
          'font-semibold text-text-faint')}>
          esc
        </kbd>
        <button
          type="button"
          aria-label="Close"
          onClick={attemptClose}
          className="ml-1 flex h-7 w-7 items-center justify-center rounded-field text-text-muted hover:text-text"
        >
          <IconClose size={18} />
        </button>
      </span>
    </header>
  )

  const body = (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto',
      phone ? 'px-4 pb-6 pt-4' : 'px-5 pb-5 pt-[18px]')}>
      {children}
    </div>
  )

  /*
   * Three answers, not two. "Discard changes" is a text link on the left
   * because it is the one that loses work, and a red button beside a green
   * one is a coin toss at speed.
   */
  const dialog = asking && (
    <ConfirmDialog
      title="Close without saving?"
      body={`${title} has ${changeCount && changeCount > 0
        ? `${changeCount} change${changeCount === 1 ? '' : 's'}`
        : 'changes'} that will be lost. Save it, keep editing, or throw the changes away.`}
      confirmLabel={saveLabel}
      cancelLabel="Keep editing"
      layer={layer}
      tone="default"
      extra={(
        <button
          type="button"
          onClick={() => { setAsking(false); onClose() }}
          className="mr-auto text-body font-semibold text-danger hover:underline"
        >
          Discard changes
        </button>
      )}
      onConfirm={() => { setAsking(false); onSave?.() }}
      onCancel={() => setAsking(false)}
    />
  )

  return createPortal(
    <>
        <div
          style={{ zIndex: layer }}
          data-overlay-layer={layer}
          className={cn('fixed inset-0 flex justify-end bg-text/[.18]')}
          onMouseDown={(event) => { if (event.target === event.currentTarget) attemptClose() }}
        >
          <div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            style={phone ? undefined : { width: showAside ? width + asideWidth : width, maxWidth: '100%' }}
            className={cn('flex h-full border-l border-line bg-surface shadow-slideover',
              phone && 'w-full', aside !== undefined && 'transition-[width] duration-200 ease-out')}
          >
            {showAside && (
              <div
                style={{ width: asideWidth }}
                className="flex h-full min-h-0 shrink-0 flex-col border-r border-line bg-surface-alt"
              >
                {aside}
              </div>
            )}
            <div
              style={showAside ? { width } : undefined}
              className={cn('flex h-full min-w-0 flex-col', showAside ? 'shrink-0' : 'flex-1')}
            >
            {/*
              * On a phone the header *is* the footer: Cancel, the title, and
              * Save in one 56px bar, because a sticky footer under a full-screen
              * form fights the keyboard for the bottom of the screen.
              */}
            {phone
              ? (
                <header className={cn('flex h-14 shrink-0 items-center gap-3 border-b',
                  'border-line px-4')}>
                  <button
                    type="button"
                    onClick={attemptClose}
                    className="shrink-0 text-[15px] font-semibold text-text-muted"
                  >
                    Cancel
                  </button>
                  <div className="flex min-w-0 flex-1 flex-col items-center">
                    <b className="truncate text-[17px] font-bold text-text">{title}</b>
                    {sub && <span className="truncate text-[11.5px] text-text-muted">{sub}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => onSave?.()}
                    className="shrink-0 text-[15px] font-bold text-accent"
                  >
                    {saveLabel}
                  </button>
                </header>
              )
              : header}

            {body}

            {!phone && (
              <footer className={cn('flex shrink-0 items-center border-t border-line',
                'bg-surface-alt px-5', roomyFooter ? 'gap-2.5 py-3.5' : 'gap-2 py-3')}>
                <span className="min-w-0 truncate text-[12px] text-text-faint">
                  {note ?? <><Keycaps /> saves</>}
                </span>
                <span className={cn('ml-auto flex shrink-0 items-center', roomyFooter ? 'gap-2.5' : 'gap-2')}>
                  <Button onClick={attemptClose}
                    className={roomyFooter ? 'h-9 px-3.5 text-[13px]' : 'h-[34px]'}>Cancel</Button>
                  {secondary}
                  {primary}
                </span>
              </footer>
            )}
            </div>
          </div>
        </div>

      {/*
        * Inside the portal, after the panel.
        *
        * `Modal` is not portalled — it renders where it sits in the React
        * tree. Rendered beside this component, the dialog landed *before* the
        * sheet's scrim in the document, and with both at z-50 the scrim won:
        * the buttons drew on top but could not be clicked.
        */}
      {dialog}
    </>,
    document.body,
  )
}

/** ⌘ and ↵ as two keycaps in one box, the way the mockup's footer draws them. */
function Keycaps() {
  return (
    <kbd className={cn('mr-1 inline-flex items-center gap-1 rounded-tag border border-line',
      'px-1.5 py-0.5 text-[10.5px] font-semibold text-text-faint')}>
      <span aria-hidden>⌘</span>
      <span aria-hidden>↵</span>
      <span className="sr-only">Command Enter</span>
    </kbd>
  )
}
